import { Link } from 'react-router-dom';
import { BedDouble, Bath, Maximize } from 'lucide-react';
import { schemeImage, slugify } from '../../lib/site';

export interface Listing {
  id: string;
  slug: string;
  headline: string;
  description?: string | null;
  advertisedRentMinor: number;
  advertisedRentCurrency: string;
  rentPeriod: string;
  depositMinor?: number | null;
  depositCurrency?: string;
  minTermMonths?: number;
  availableFrom: string | null;
  status: string;
  media?: string[];
  // Marketing a flat (Task L) — what the advert says. The pictures are the flat's media folders: Photos in the order
  // staff set (the first is the cover), each with what it shows ("Bedroom 2", null when unlabelled) and a caption.
  photos?: ListingPhoto[];
  floorplan?: ListingDoc | null;
  floorplans?: ListingDoc[];
  epcGraph?: ListingDoc | null;
  furnished?: string | null; letType?: string; priceQualifier?: string; holdingDepositMinor?: number | null; propertyType?: string | null;
  displayAddress?: string | null; receptionRooms?: number | null; floorLevel?: string | null; outsideSpace?: string | null; parking?: string | null;
  councilTaxBand?: string | null; epcRating?: string | null; epcCurrent?: number | null; epcPotential?: number | null;
  petsAllowed?: boolean | null; billsIncluded?: boolean | null; billsDetail?: string | null; accessibility?: string | null;
  features?: string[]; viewingArrangements?: string | null; virtualTourUrl?: string | null; videoUrl?: string | null;
  unit?: { bedrooms: number; bathrooms: number; areaSqft: number | null; unitType: string; isFurnished?: boolean; features?: string[] };
  property?: { name: string; slug?: string; city: string | null; region: string; postcode?: string | null; amenities?: string[] };
}

export interface ListingPhoto { url: string; thumbUrl?: string; label?: string | null; caption: string | null; width: number | null; height: number | null }
export interface ListingDoc { url: string; mimeType: string; thumbUrl?: string; label?: string | null; caption?: string | null }

export function formatRent(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format(minor / 100);
}

export const bedsLabel = (n?: number) => (n === 0 ? 'Studio' : n === 1 ? '1 bed' : `${n ?? '—'} bed`);

/** A home on the website. Photography comes from the scheme; the numbers come from the listing. */
export function ListingCard({ listing, index = 0, image: override }: { listing: Listing; index?: number; image?: string }) {
  const rent = formatRent(listing.advertisedRentMinor, listing.advertisedRentCurrency ?? 'GBP');
  const image = listing.media?.[0] ?? override ?? schemeImage(listing.property?.slug ?? slugify(listing.property?.name), index);
  const period = listing.rentPeriod === 'YEAR' ? 'pa' : 'pcm';
  return (
    <Link to={`/rent/${listing.slug}`} className="site-card group block min-w-0 rounded-[14px] border border-sand-200/80 p-2.5 transition duration-300 hover:-translate-y-0.5 hover:border-sand-300">
      <div className="relative aspect-[4/3] overflow-hidden rounded-[10px] bg-ink-100">
        <img src={image} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-safe:lg:group-hover:scale-[1.05]" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink-900/75 via-ink-900/20 to-transparent" />
        <div className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1.5 text-[12px] font-semibold text-ink-900 shadow-sm backdrop-blur-sm">{rent} <span className="font-medium text-ink-500">{period}</span></div>
        <div className="absolute bottom-4 left-4 right-4 min-w-0 text-white">
          <div className="truncate text-[11px] font-medium uppercase tracking-[0.18em] text-white/80">{listing.property?.city}</div>
          <div className="h-card mt-1 truncate text-white">{listing.property?.name}</div>
        </div>
      </div>
      <div className="flex items-start justify-between gap-3 px-2 pb-2 pt-3.5">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[15px] font-medium text-ink-900 transition-colors group-hover:text-bronze-600">{listing.headline}</div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-500">
            <span className="flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" />{bedsLabel(listing.unit?.bedrooms)}</span>
            <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" />{listing.unit?.bathrooms ?? 1} bath</span>
            {listing.unit?.areaSqft && <span className="flex items-center gap-1"><Maximize className="h-3.5 w-3.5" />{listing.unit.areaSqft} sq ft</span>}
          </div>
        </div>
        <div className="shrink-0 pt-0.5 text-right text-[11px] text-ink-400">
          {listing.availableFrom && new Date(listing.availableFrom) > new Date()
            ? `Available from ${new Date(listing.availableFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
            : 'Available now'}
        </div>
      </div>
    </Link>
  );
}
